import { loginRequest, msalInstance } from '@/components/auth/authConfig';
import { GraphUser } from '@/types/planner';
import axios, { Axios, AxiosError } from 'axios';
import { logApi } from './apihelper';
import { FeatureFlags } from '@/common/FeatureFlags';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { MissingTokenError, NoActiveAccountError } from '@/common/errors';
import { appInsights } from '@/appInsights';
import { hashString } from '@/common/utils';

export default class MsGraphApiClient {
  readonly #client: Axios;

  constructor() {
    this.#client = axios.create({
      baseURL: 'https://graph.microsoft.com/v1.0/',
      adapter: 'fetch',
      headers: {
        Accept: 'application/json',
      },
    });

    this.#client.interceptors.request.use(async (config) => {
      let account = msalInstance.getActiveAccount();
      if (!account) {
        const foundAccount = msalInstance.getAccount({
          tenantId: import.meta.env.VITE_AZURE_TENANT_ID,
        });

        if (foundAccount) {
          msalInstance.setActiveAccount(foundAccount);
        }

        account = msalInstance.getActiveAccount();
        if (!account) {
          await msalInstance.loginRedirect();
          throw new NoActiveAccountError();
        }
      }

      if (FeatureFlags.logRequests) {
        logApi('ms', config.url ?? '', config.method);
      }

      try {
        const tokenResponse = await msalInstance.acquireTokenSilent({
          ...loginRequest,
          account,
        });

        if (!tokenResponse.accessToken) {
          throw new MissingTokenError('Missing access token in token response');
        }

        config.headers['Authorization'] = `Bearer ${tokenResponse.accessToken}`;
      } catch (err) {
        if (err instanceof Error) {
          console.error(err);
          if (err instanceof InteractionRequiredAuthError || err instanceof MissingTokenError) {
            await msalInstance.loginRedirect(loginRequest);
          }
        }
      }

      return config;
    });

    if (FeatureFlags.logRequests) {
      this.#client.interceptors.response.use(
        async (response) => {
          logApi('ms', `${response.config.url}`, response.config.method, true, response.status);
          return response;
        },
        (error) => {
          if (error instanceof AxiosError) {
            logApi(
              'ms',
              `${error?.response?.config.url}`,
              error?.response?.config.method,
              true,
              error?.response?.status,
            );
          }

          return Promise.reject(error);
        },
      );
    }
  }

  async getUserInfo() {
    const response = await this.#client.get<GraphUser>('/me');

    try {
      const userHash = await hashString(response.data.id);
      appInsights.setAuthenticatedUserContext(userHash, userHash, false);
    } catch {
      // No need to do anything here
    }

    return response.data;
  }

  async getUserPhoto(): Promise<string | null> {
    try {
      const response = await this.#client.get<Blob>('/me/photo/$value', {
        responseType: 'blob',
      });
      return URL.createObjectURL(response.data);
    } catch {
      return null;
    }
  }
}

export const defaultMsGraphClient = new MsGraphApiClient();
